import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import Logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/context/AuthContext';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import ClubUiProviders from '../providers/ClubUiProviders';

const HERO_IMAGE =
    'https://images.unsplash.com/photo-1607082349566-187342175e2f?auto=format&fit=crop&w=2000&q=80';

const clubLoginSchema = z.object({
    username: z.string().min(1, 'Usuário é obrigatório'),
    password: z.string().min(1, 'Informe a senha'),
});

function ClubLoginForm() {
    const [showPassword, setShowPassword] = useState(false);
    const { setErrors } = useClubErrors();
    const { Login } = useAuth();

    const form = useForm({
        resolver: zodResolver(clubLoginSchema),
        defaultValues: {
            username: '',
            password: '',
        },
    });

    const onSubmit = async values => {
        try {
            const { data } = await server.post('/club/auth', {
                username: values.username.trim(),
                password: values.password,
            });

            const payload = data.data || data;
            await Login({
                token: payload.token,
                refreshtoken: payload.refreshToken,
                user: payload.user,
                club: payload.club || payload.user?.club,
            });
        } catch (error) {
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;

    return (
        <div className="flex min-h-svh flex-col md:flex-row">
            <div
                className="relative flex min-h-48 flex-col justify-end bg-cover bg-center p-6 text-white md:min-h-svh md:w-1/2 md:p-10"
                style={{
                    backgroundImage: `linear-gradient(145deg, rgba(59, 91, 219, 0.82) 0%, rgba(15, 23, 42, 0.75) 50%, rgba(2, 6, 23, 0.88) 100%), url(${HERO_IMAGE})`,
                }}
            >
                <h1 className="max-w-md text-2xl font-extrabold tracking-tight md:text-4xl">
                    Painel do gestor do clube
                </h1>
                <p className="mt-3 max-w-md text-sm leading-relaxed opacity-95 md:text-base">
                    Gerencie assinantes, planos e a experiência do seu clube de benefícios em um só lugar.
                </p>
            </div>

            <div className="flex flex-1 flex-col justify-center px-4 py-8 md:w-1/2 md:px-10">
                <div className="mx-auto flex w-full max-w-md flex-col gap-6">
                    <img
                        src={Logo}
                        alt="Eurobem"
                        className="block h-auto w-[136px] max-w-full shrink-0 object-contain object-left"
                    />

                    <div className="flex flex-col gap-1">
                        <h2 className="text-xl font-bold tracking-tight">Login do gestor</h2>
                        <p className="text-sm text-muted-foreground">
                            Entre com seu usuário e senha. Se você gerencia mais de um clube, pode trocar depois no
                            painel.
                        </p>
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle>Entrar no painel</CardTitle>
                            <CardDescription>
                                Use as credenciais cadastradas. Em dúvidas, fale com o suporte Eurobem.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                                <FieldGroup>
                                    <Field data-invalid={Boolean(errors.username)}>
                                        <FieldLabel htmlFor="username">Usuário</FieldLabel>
                                        <Controller
                                            name="username"
                                            control={form.control}
                                            render={({ field }) => (
                                                <Input
                                                    id="username"
                                                    autoComplete="username"
                                                    aria-invalid={Boolean(errors.username)}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                        />
                                        <FieldError errors={[errors.username]} />
                                    </Field>

                                    <Field data-invalid={Boolean(errors.password)}>
                                        <FieldLabel htmlFor="password">Senha</FieldLabel>
                                        <div className="relative">
                                            <Controller
                                                name="password"
                                                control={form.control}
                                                render={({ field }) => (
                                                    <Input
                                                        id="password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        autoComplete="current-password"
                                                        aria-invalid={Boolean(errors.password)}
                                                        className="pr-10"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                    />
                                                )}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon-sm"
                                                className="absolute top-1/2 right-1 -translate-y-1/2"
                                                onClick={() => setShowPassword(prev => !prev)}
                                                aria-label="Alternar visibilidade da senha"
                                            >
                                                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                            </Button>
                                        </div>
                                        <FieldError errors={[errors.password]} />
                                    </Field>

                                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                                        {isSubmitting ? <Spinner data-icon="inline-start" /> : null}
                                        Entrar
                                    </Button>
                                </FieldGroup>
                            </form>
                        </CardContent>
                        <CardFooter className="justify-center border-t-0 bg-transparent pt-0">
                            <Link to="/recuperar-senha?profile=club" className="text-xs font-semibold text-primary">
                                Esqueci minha senha
                            </Link>
                        </CardFooter>
                    </Card>

                    <Link to="/?profile=club" className="text-center text-xs text-muted-foreground">
                        Voltar para seleção de perfil
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default function ClubLogin() {
    return (
        <ClubUiProviders rootId="club-login-root">
            <ClubLoginForm />
        </ClubUiProviders>
    );
}
